import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { type CheckList } from './types';
import { GSCheckListBox } from './GSCheckListBox';
import { PAGE_TEXT } from '../../../lang';
import {
  useCreateGsDecision,
  useGetDsrcApproval,
  useGetFrcAssessment,
  useGetStudentAcademic,
  useGetStudentInfo,
} from '../../../graphql';
import {
  Accordion,
  Alert,
  Loading,
  type AlertDetails,
} from '../../../../../components';

type GSCheckListProps = {
  title: string;
  hadGsChecked: boolean;
  department: string;
  refetchGsChecked: () => void;
};

export const GSCheckList: React.FunctionComponent<GSCheckListProps> = props => {
  const { title, department, hadGsChecked, refetchGsChecked } = props;
  const { applicationId = '' } = useParams();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const {
    studentAcademic,
    studentLanguage,
    studentOtherTest,
    studentAssessmentDetail,
    studentAcademicReferees,
    returnedRemarks,
    loading: studentAcademicLoading,
    error: studentAcademicError,
  } = useGetStudentAcademic(applicationId);

  const {
    studentInfo,
    meritAssessments,
    publicationAssessments,
    loading: studentInfoLoading,
    error: studentInfoError,
  } = useGetStudentInfo(applicationId, department);

  const {
    frcAssessment,
    loading: frcAssessmentLoading,
    error: frcAssessmentError,
  } = useGetFrcAssessment({
    applicationId,
  });

  const {
    dsrcApproval,
    drcRequired,
    loading: dsrcApprovalLoading,
    error: dsrcApprovalError,
  } = useGetDsrcApproval(applicationId);

  const finishCheck = useCreateGsDecision();
  const handleSubmit = async (value: CheckList): Promise<void> => {
    try {
      const variables = {
        input: {
          applicationId,
          allItemChecked: true,
        },
      };
      await finishCheck({
        variables,
      });
      setAlertDetails({
        alertContent: PAGE_TEXT.GS_ASSESSMENT_DETAILS.CHECK.SUCCESS_NOTE,
        alertType: 'success',
      });
      setOpenAlert(true);
      refetchGsChecked();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Error Occurs.${error?.message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const loading =
    studentAcademicLoading ||
    studentInfoLoading ||
    frcAssessmentLoading ||
    dsrcApprovalLoading;

  if (
    studentAcademicError ??
    studentInfoError ??
    frcAssessmentError ??
    dsrcApprovalError
  ) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            'Error Occurs.' +
            (studentAcademicError as Error)?.message +
            (studentInfoError as Error)?.message +
            (frcAssessmentError as Error)?.message +
            (dsrcApprovalError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const studentData = {
    studentInfo,
    studentAcademic,
    studentLanguage,
    studentOtherTest,
    studentAcademicReferees,
  };
  const studentAssessment = {
    frcAssessment,
    dsrcAssessment: {
      dsrcApproval,
      drcRequired,
    },
    studentAssessmentDetail,
    meritAssessments,
    publicationAssessments,
  };

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      {loading ? (
        <Loading />
      ) : (
        <ContentBox>
          <Accordion
            className="w-full"
            defaultExpanded
            title={`${PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT.TITLE} - ${title}`}
            details={
              <GSCheckListBox
                onSubmit={handleSubmit}
                hadGsChecked={hadGsChecked}
                studentAssessment={studentAssessment}
                studentData={studentData}
                returnedRemarks={returnedRemarks}
              />
            }
          />
        </ContentBox>
      )}
    </div>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 1rem;

  Accordion {
    margin-bottom: 1rem;
  }
`;
